import { ApiResponse, PaginatedCollection, directusApi } from '@/api';
import { validateFileName } from '@/utils';
import lodash from 'lodash';

export interface Promo {
    id?: number;
    title: string;
    description: string;
    link: string;
    image?: any;
    start_date: string;
    end_date: string;
    visible: boolean;
}

export interface PromoState {
    activePromo: Promo | null;
    activePromoLoading: boolean;

    allPromos: Array<Promo>;
    allPromosLoading: boolean;
    promosPerPage: number;
    promosPage: number;
    promosTotal: number;
}

const state: PromoState = {
    activePromo: {
        id: 0,
        title: '',
        description: '',
        link: '',
        image: [],
        start_date: '',
        end_date: '',
        visible: false
    },
    activePromoLoading: false,

    allPromos: [],
    allPromosLoading: false,
    promosPerPage: 1,
    promosPage: 1,
    promosTotal: 1,
};

const defaultPromo = {
    id: 0,
    title: '',
    description: '',
    link: '',
    image: null,
    start_date: null,
    end_date: null,
    visible: false
}

const mutations = {
    TOGGLE_ALLPROMOS_LOADING(state: PromoState, data: boolean) {
        state.allPromosLoading = data;
    },
    SET_ALLPROMOS(state: PromoState, data: PaginatedCollection) {
        state.allPromos = data.items;
        state.promosPerPage = data.perPage;
        state.promosPage = data.currentPage;
        state.promosTotal = data.totalItems;
    },
    TOGGLE_ACTIVE_PROMO_LOADING(state: PromoState, data: boolean) {
        state.activePromoLoading = data;
    },
    REMOVE_PROMO(state: PromoState, id: number) {
        state.allPromos = lodash.remove(state.allPromos, function (n) {
            return n.id != id;
        });
    },
    SET_ACTIVE_PROMO(state: PromoState, data: Promo) {
        let image = data.image ? { id: data.image, url: `https://directus.prodelec.app/assets/${data.image}`, origin: 'directus' } : null;
        state.activePromo = {
            ...data,
            image
        };
    },
};

const actions = {
    async getAll(
        { _, commit }: any,
        {
            search,
            page,
        }: any | null
    ) {
        commit('TOGGLE_ALLPROMOS_LOADING', true);

        let limit = 20;
        let params = '';
        if (search) {
            params += `&search=${search}`;
        }
        params += `&meta=total_count&meta=filter_count`;
        if (page) params += `&limit=20&offset=${(page - 1) * limit}`;

        let endpoint = `items/store_promos?${params}`;

        await directusApi('GET', endpoint)
            .then(res => {
                commit('SET_ALLPROMOS', {
                    items: res.data.data,
                    perPage: limit,
                    currentPage: page,
                    totalItems: res.data.meta.filter_count,
                });
            })
            .catch(err => {
                console.log(err);
            })
            .finally(() => {
                commit('TOGGLE_ALLPROMOS_LOADING', false);
            });
    },

    async getOne({ _, commit }: any, id: any | null = null) {
        commit('TOGGLE_ACTIVE_PROMO_LOADING', true);
        if (id) {
            await directusApi('GET', `items/store_promos/${id}`)
                .then(res => {
                    commit('SET_ACTIVE_PROMO', res.data.data);
                })
                .catch(err => {
                    console.log(err);
                })
                .finally(() => {
                    commit('TOGGLE_ACTIVE_PROMO_LOADING', false);
                });
        } else {
            commit('SET_ACTIVE_PROMO', defaultPromo);
            commit('TOGGLE_ACTIVE_PROMO_LOADING', false);
        }
    },

    async postOne({ _, commit, dispatch }: any, data: any) {
        commit('TOGGLE_ACTIVE_PROMO_LOADING', true);

        let promoEndpoint: string = 'items/store_promos';
        let promoMethod: any = 'POST';

        if (data.data.id != 0) {
            promoMethod = 'PATCH';
            promoEndpoint = `items/store_promos/${data.data.id}`;
        }

        await directusApi(promoMethod, promoEndpoint, {
            title: data.data.title,
            description: data.data.description,
            status: data.data.status,
            start_date: data.data.start_date,
            end_date: data.data.end_date,
            link: data.data.link,
            visible: data.data.visible
        })
            .then(async (promo: any) => {

                let image = {};
                data.data.image == '' ? image = null : image = promo.data.data.image;

                try {
                    if (data.data.image.origin == 'upload') {
                        let imageNameValidation = await validateFileName(data.data.image.name);
                        if (imageNameValidation == 0) {
                            console.log('uploading', data.data.image);
                            await directusApi('POST', `files`, data.data.image.file)
                                .then(async (resFile: any) => {
                                    image = { id: resFile.data.data.id };
                                })
                                .catch(err => {
                                    console.log(err);
                                });
                        } else {
                            data.vm.$bvToast.toast(`Error al guardar, la imagen ${data.data.image.name} ya existe.`, {
                                title: `Imagen ya existente`,
                                variant: 'warning',
                                toaster: 'b-toaster-top-center',
                                autoHideDelay: 5000,
                            });
                            commit('TOGGLE_ACTIVE_PROMO_LOADING', false);
                            return false;
                        }
                    } else if (data.data.image.origin == 'directus-update') {
                        console.log('img only setted');
                        image = { id: data.data.image.id };
                    }
                } catch (error) {
                    console.log('error => no contiene una imagen');
                }

                // ********************** START adding id of img to promo
                await directusApi('PATCH', `items/store_promos/${promo.data.data.id}`, { image })
                    .then(async (res: any) => {
                        console.log('image added/updated', res.data.data.image);
                    })
                    .catch(err => {
                        console.log(err);
                    });
                // ********************** END adding id of img to promo

                dispatch('getOne', promo.data.data.id);
                data.vm.$bvToast.toast('Información agregada/actualizada exitosamente', {
                    title: `Obteniendo información`,
                    variant: 'success',
                    toaster: 'b-toaster-top-center',
                    autoHideDelay: 5000,
                });

                commit('TOGGLE_ACTIVE_PROMO_LOADING', false);
                dispatch("getAll", {
                    page: 1,
                    search: ''
                });
                commit('SET_ACTIVE_PROMO', promo.data.data);
            })
            .catch(err => {
                console.log(err);
            })
            .finally(() => {
                commit('TOGGLE_ACTIVE_PROMO_LOADING', false);
            });
    },
    async deleteOne({ _, commit, dispatch }: any, data: any) {
        commit('TOGGLE_ACTIVE_PROMO_LOADING', true);
        await directusApi('DELETE', `items/store_promos/${data.id}`, {})
            .then(async (res: any) => {
                data.vm.$bvToast.toast('Se ha eliminado exitosamente', {
                    title: `Promoción eliminada`,
                    variant: 'success',
                    toaster: 'b-toaster-top-center',
                    autoHideDelay: 5000,
                });
                commit('REMOVE_PROMO', data.id);
                dispatch("getAll", {
                    page: 1,
                    search: ''
                });
            })
            .catch(err => {
                console.log(err);
            })
            .finally(() => {
                commit('TOGGLE_ACTIVE_PROMO_LOADING', false);
            });
    },
    async togglePromoFalse({ _, commit }: any) {
        commit('TOGGLE_ACTIVE_PROMO_LOADING', false);
    },
};

export default {
    namespaced: true,
    state,
    actions,
    mutations,
};
